import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PanelRight, PhoneMissed } from "lucide-react";
import { useDataProvider, useNotify } from "ra-core";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";

import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";

import { getSupabaseClient } from "../../providers/supabase/supabase";
import { ClientStageBar } from "../ClientStageBar";
import { CLIENT_STAGES, labelOf } from "../gulaConstants";
import type { GulaClient } from "../gulaTypes";
import { recordResult, type QuickResult } from "../callPlan/useCallPlan";
import { useAgents } from "../useAgents";
import {
  PENDING_CALL_EVENT,
  clearPendingCall,
  readPendingCall,
  shouldAskResult,
  type PendingCall,
} from "./pendingCall";

const useClient = (id?: string) =>
  useQuery({
    queryKey: ["gula-call-result-client", id],
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await getSupabaseClient()
        .from("gula_clients_summary")
        .select("*")
        .eq("id", Number(id))
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as GulaClient | null;
    },
  });

/**
 * "¿Cómo ha ido?": pops up when the setter comes back to the CRM after
 * tapping "Llamar por WhatsApp", to write down the call before the next one.
 */
export const CallResultSheet = () => {
  const [pending, setPending] = useState<PendingCall | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const { data: client } = useClient(pending?.clientId);
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { meId } = useAgents();

  const check = useCallback(() => {
    if (document.visibilityState !== "visible") return;
    const current = readPendingCall();
    if (shouldAskResult(current)) {
      setPending((previous) =>
        previous?.at === current.at ? previous : current,
      );
    }
  }, []);

  useEffect(() => {
    check();
    // Coming back from WhatsApp: the tab becomes visible / the app gets focus
    document.addEventListener("visibilitychange", check);
    window.addEventListener("focus", check);
    window.addEventListener("pageshow", check);
    // A call started in this page: ask once they come back (MIN_AWAY_MS)
    const onStart = () => window.setTimeout(check, 3500);
    window.addEventListener(PENDING_CALL_EVENT, onStart);
    return () => {
      document.removeEventListener("visibilitychange", check);
      window.removeEventListener("focus", check);
      window.removeEventListener("pageshow", check);
      window.removeEventListener(PENDING_CALL_EVENT, onStart);
    };
  }, [check]);

  const close = () => {
    clearPendingCall();
    setPending(null);
    setNote("");
  };

  const saveNote = async () => {
    const text = note.trim();
    if (!text || !client) return;
    await dataProvider.create("contact_notes", {
      data: {
        contact_id: client.contact_id,
        text,
        date: new Date().toISOString(),
        sales_id: meId ?? null,
      },
    });
  };

  const refresh = () => queryClient.invalidateQueries();

  const quick = async (result: QuickResult) => {
    if (!pending) return;
    setBusy(true);
    try {
      await saveNote();
      await recordResult(pending.clientId, result);
      notify(
        result === "no_answer"
          ? `${pending.name}: no contesta (+1). Vuelve a tu bolsa más tarde`
          : `${pending.name}: hecho`,
        { type: "success" },
      );
      close();
      refresh();
    } catch (error) {
      notify(error instanceof Error ? error.message : "No se ha podido", {
        type: "error",
      });
    } finally {
      setBusy(false);
    }
  };

  const openFicha = async () => {
    if (!pending) return;
    try {
      await saveNote();
    } catch {
      // the note box stays in the ficha anyway
    }
    const id = pending.clientId;
    close();
    refresh();
    navigate(`/?cliente=${id}`);
  };

  if (!pending) return null;

  return (
    <Sheet open onOpenChange={(open) => !open && close()}>
      <SheetContent
        side="bottom"
        className="max-h-[92dvh] gap-0 overflow-y-auto rounded-t-3xl pb-[max(1rem,env(safe-area-inset-bottom))]"
      >
        <SheetHeader className="pb-2">
          <SheetTitle className="text-xl">
            ¿Cómo ha ido con {pending.name}?
          </SheetTitle>
          <SheetDescription>
            {client
              ? `Ahora en ${labelOf(CLIENT_STAGES, client.stage)}. Apunta lo que habéis hablado y marca el resultado.`
              : "Apunta lo que habéis hablado y marca el resultado."}
          </SheetDescription>
        </SheetHeader>
        <div className="flex flex-col gap-3 px-4">
          <Textarea
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder="Qué te ha dicho: dudas, precio, cuándo volver a llamar… (puedes dictar con el micro del teclado)"
            className="min-h-24 text-base"
            disabled={busy}
          />
          <Button
            variant="outline"
            className="h-14 rounded-2xl text-base"
            disabled={busy}
            onClick={() => quick("no_answer")}
          >
            <PhoneMissed className="h-5 w-5" />
            No contesta
          </Button>
          {client ? (
            <ClientStageBar
              client={client}
              onChanged={async () => {
                try {
                  await saveNote();
                } catch {
                  notify("La fase se ha guardado, pero la nota no", {
                    type: "warning",
                  });
                }
                close();
                refresh();
              }}
            />
          ) : null}
          <div className="flex items-center justify-between gap-2 pb-2">
            <Button variant="ghost" onClick={openFicha} disabled={busy}>
              <PanelRight className="h-4 w-4" />
              Abrir su ficha
            </Button>
            <Button
              variant="ghost"
              className="text-muted-foreground"
              onClick={close}
              disabled={busy}
            >
              No he llamado
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
